import { Injectable } from '@angular/core';
import {
    ThemeConfig,
    BackendThemeData,
    DefaultThemeConfig,
    ThemeColors,
    BrandAssets,
    ComponentStyles
} from '../interfaces/theme.interface';

@Injectable({
    providedIn: 'root'
})
export class ThemeBuilderService {

    /**
     * Construye un tema completo a partir de los datos del backend
     * Si los datos están vacíos, usa los valores por defecto
     */
    buildFromBackendData(backendData: BackendThemeData, defaultTheme: DefaultThemeConfig): ThemeConfig {
        const safeBackend = backendData ?? {};
        const brandId = this.getNonEmptyString(safeBackend.brandId) ?? 'default';
        const brandName = this.getNonEmptyString(safeBackend.brandName) ?? 'Tema por defecto';
        const descriptionName = this.getNonEmptyString(safeBackend.brandName) ?? 'la aplicación';
        return {
            id: brandId,
            name: brandName,
            description: `Tema personalizado para ${descriptionName}`,
            colors: this.buildColors(safeBackend, defaultTheme.colors),
            typography: defaultTheme.typography, // Por ahora usamos la tipografía por defecto
            spacing: defaultTheme.spacing,
            borderRadius: defaultTheme.borderRadius,
            shadows: defaultTheme.shadows,
            breakpoints: defaultTheme.breakpoints,
            assets: this.buildAssets(safeBackend, defaultTheme.assets),
            components: this.buildComponents(safeBackend, defaultTheme.components),
            customCSS: this.getNonEmptyString(safeBackend.customCSS),
            isDefault: !this.getNonEmptyString(safeBackend.brandId)
        };
    }

    /**
     * Construye la paleta de colores combinando datos del backend con valores por defecto
     */
    private buildColors(backendData: BackendThemeData, defaultColors: ThemeColors): ThemeColors {
        // Solo copiamos los colores esenciales que siempre se necesitan
        const colors: ThemeColors = {
            // Colores siempre necesarios
            primary: defaultColors.primary,
            primaryLight: defaultColors.primaryLight,
            primaryDark: defaultColors.primaryDark,
            // NO copiamos secondary por defecto - se agrega solo si hay personalización

            // Colores de estado (siempre necesarios)
            success: defaultColors.success,
            warning: defaultColors.warning,
            error: defaultColors.error,
            info: defaultColors.info,

            // Colores de fondo (siempre necesarios)
            background: defaultColors.background,
            backgroundSecondary: defaultColors.backgroundSecondary,
            surface: defaultColors.surface,
            surfaceVariant: defaultColors.surfaceVariant,

            // Colores de texto (siempre necesarios)
            textSecondary: defaultColors.textSecondary,
            textMuted: defaultColors.textMuted,
            textOnPrimary: defaultColors.textOnPrimary,
            textOnSecondary: defaultColors.textOnSecondary,
            textOnBackground: defaultColors.textOnBackground,
            textOnSurface: defaultColors.textOnSurface,
            // NO copiamos textPrimary por defecto - se agrega solo si hay personalización específica

            // Colores UI (siempre necesarios)
            border: defaultColors.border,
            borderLight: defaultColors.borderLight,
            divider: defaultColors.divider,
            disabled: defaultColors.disabled,
            hover: defaultColors.hover,
            focus: defaultColors.focus,
            active: defaultColors.active,
            shadow: defaultColors.shadow,
            overlay: defaultColors.overlay

            // Los colores específicos de componentes NO se copian aquí
            // Se agregan solo si hay personalización
        };

        // Si hay color primario del backend, usarlo y generar variantes
        const primaryColor = this.getNonEmptyString(backendData.primaryColor);
        if (primaryColor) {
            colors.primary = primaryColor;
            colors.primaryLight = this.lightenColor(primaryColor, 0.2);
            colors.primaryDark = this.darkenColor(primaryColor, 0.2);
        }

        // Si hay color secundario del backend, usarlo y generar variantes
        const secondaryColor = this.getNonEmptyString(backendData.secondaryColor);
        if (secondaryColor) {
            colors.secondary = secondaryColor;
            colors.secondaryLight = this.lightenColor(secondaryColor, 0.2);
            colors.secondaryDark = this.darkenColor(secondaryColor, 0.2);
        }

        // Usar color de acento si está disponible
        const accentColor = this.getNonEmptyString(backendData.accentColor);
        if (accentColor) {
            colors.active = accentColor;
            colors.focus = accentColor;
        }

        // Usar color de borde específico si está disponible (como Point S #00BD3F)
        const borderColor = this.getNonEmptyString(backendData.borderColor);
        if (borderColor) {
            colors.border = borderColor;
            colors.borderLight = this.lightenColor(borderColor, 0.3);
        }

        // Usar color de texto principal específico si está disponible (como Point S #213BA7)
        const textPrimaryColor = this.getNonEmptyString(backendData.textPrimaryColor);
        if (textPrimaryColor) {
            colors.textPrimary = textPrimaryColor;
        }

        // Usar color de fondo para día actual específico si está disponible (como Point S #CCD3EF)
        const todayBackgroundColor = this.getNonEmptyString(backendData.todayBackgroundColor);
        if (todayBackgroundColor) {
            colors.todayBackground = todayBackgroundColor;
        }

        // Usar color específico para enlaces si está disponible (como Point S #00BD3F)
        const linkColor = this.getNonEmptyString(backendData.linkColor);
        if (linkColor) {
            colors.linkColor = linkColor;
        }

        // Usar colores específicos para contador de clientes si están disponibles
        const clientCountBgColor = this.getNonEmptyString(backendData.clientCountBgColor);
        if (clientCountBgColor) {
            colors.clientCountBg = clientCountBgColor;
        }

        const clientCountTextColor = this.getNonEmptyString(backendData.clientCountTextColor);
        if (clientCountTextColor) {
            colors.clientCountText = clientCountTextColor;
        }

        // Usar color específico para fondo de barra superior si está disponible
        const topbarBgColor = this.getNonEmptyString(backendData.topbarBgColor);
        if (topbarBgColor) {
            colors.topbarBg = topbarBgColor;
        }

        // Usar color específico para fondo de tarjeta de perfil si está disponible
        const profileCardBgColor = this.getNonEmptyString(backendData.profileCardBgColor);
        if (profileCardBgColor) {
            colors.profileCardBg = profileCardBgColor;
        }

        // Usar color específico para fondo del botón flecha si está disponible
        const arrowButtonBgColor = this.getNonEmptyString(backendData.arrowButtonBgColor);
        if (arrowButtonBgColor) {
            colors.arrowButtonBg = arrowButtonBgColor;
        }

        // Usar color específico para hover del botón flecha si está disponible
        const arrowButtonHoverBgColor = this.getNonEmptyString(backendData.arrowButtonHoverBgColor);
        if (arrowButtonHoverBgColor) {
            colors.arrowButtonHoverBg = arrowButtonHoverBgColor;
        }

        // Usar configuraciones específicas del KPI de pedidos pendientes si están disponibles
        const pendingOrdersBackgroundColor = this.getNonEmptyString(backendData.pendingOrdersBackgroundColor);
        if (pendingOrdersBackgroundColor) {
            colors.pendingOrdersBackgroundColor = pendingOrdersBackgroundColor;
        }

        const pendingOrdersBorder = this.getNonEmptyString(backendData.pendingOrdersBorder);
        if (pendingOrdersBorder) {
            colors.pendingOrdersBorder = pendingOrdersBorder;
        }

        // Usar avatar personalizado del usuario si está disponible
        const userAvatarUrl = this.getNonEmptyString(backendData.userAvatarUrl);
        if (userAvatarUrl) {
            colors.userAvatarUrl = userAvatarUrl;
        }

        // Usar chevron personalizado si está disponible
        const chevronImageUrl = this.getNonEmptyString(backendData.chevronImageUrl);
        if (chevronImageUrl) {
            colors.chevronImageUrl = chevronImageUrl;
        }

        // Usar logo personalizado del topbar si está disponible
        const topbarLogoUrl = this.getNonEmptyString(backendData.topbarLogoUrl);
        if (topbarLogoUrl) {
            colors.topbarLogoUrl = topbarLogoUrl;
        }

        return colors;
    }

    /**
     * Construye los assets combinando datos del backend con valores por defecto
     */
    private buildAssets(backendData: BackendThemeData, defaultAssets: BrandAssets): BrandAssets {
        const logoUrl = this.getNonEmptyString(backendData.logoUrl);
        const logoWhiteUrl = this.getNonEmptyString(backendData.logoWhiteUrl);
        const logoSmallUrl = this.getNonEmptyString(backendData.logoSmallUrl);
        const faviconUrl = this.getNonEmptyString(backendData.faviconUrl);
        const backgroundImageUrl = this.getNonEmptyString(backendData.backgroundImageUrl);
        const assetBaseSource = logoUrl || logoWhiteUrl || logoSmallUrl || faviconUrl || backgroundImageUrl;

        return {
            ...defaultAssets,
            logo: logoUrl || defaultAssets.logo,
            logoWhite: logoWhiteUrl || defaultAssets.logoWhite,
            logoSmall: logoSmallUrl || defaultAssets.logoSmall,
            favicon: faviconUrl || defaultAssets.favicon,
            backgroundImage: backgroundImageUrl || defaultAssets.backgroundImage,
            assetsBaseUrl: this.extractBaseUrl(assetBaseSource) || defaultAssets.assetsBaseUrl
        };
    }

    /**
     * Construye los estilos de componentes basados en los datos del backend
     */
    private buildComponents(backendData: BackendThemeData, defaultComponents: ComponentStyles): ComponentStyles {
        const components: ComponentStyles = this.cloneComponents(defaultComponents);

        // Aplicar color primario a los botones si está disponible
        const primaryColor = this.getNonEmptyString(backendData.primaryColor);
        if (primaryColor) {
            components.buttons.primary.backgroundColor = primaryColor;
            components.buttons.primary.hoverBackgroundColor = this.darkenColor(primaryColor, 0.1);

            // Aplicar a la navegación
            components.navigation.activeColor = primaryColor;

            // Solo usar el color aclarado si no hay un color específico para sidebar activo
            if (!backendData.sidebarActiveBgColor) {
                components.sidebar.activeBackgroundColor = this.lightenColor(primaryColor, 0.9);
            }

            components.sidebar.activeTextColor = primaryColor;
        }

        // Aplicar color específico para fondo activo del sidebar si está disponible
        const sidebarActiveBgColor = this.getNonEmptyString(backendData.sidebarActiveBgColor);
        if (sidebarActiveBgColor) {
            components.sidebar.activeBackgroundColor = sidebarActiveBgColor;
        }

        // Aplicar color secundario si está disponible
        const secondaryColor = this.getNonEmptyString(backendData.secondaryColor);
        if (secondaryColor) {
            components.buttons.secondary.backgroundColor = secondaryColor;
            components.buttons.secondary.hoverBackgroundColor = this.darkenColor(secondaryColor, 0.1);
        }

        // Aplicar estilos específicos según las preferencias del backend
        const buttonStyle = this.getNonEmptyString(backendData.buttonStyle);
        if (buttonStyle) {
            const borderRadius = this.getButtonBorderRadius(buttonStyle);
            components.buttons.primary.borderRadius = borderRadius;
            components.buttons.secondary.borderRadius = borderRadius;
            components.buttons.outline.borderRadius = borderRadius;
        }

        return components;
    }

    /**
     * Obtiene el tema por defecto de la aplicación
     */
    getDefaultTheme(): DefaultThemeConfig {
        return {
            colors: {
                // Colores por defecto de Cafler (basados en los estilos actuales)
                primary: '#3b82f6',
                primaryLight: '#60a5fa',
                primaryDark: '#1d4ed8',
                secondary: '#10b981',
                secondaryLight: '#34d399',
                secondaryDark: '#059669',
                success: '#10b981',
                warning: '#f59e0b',
                error: '#ef4444',
                info: '#3b82f6',
                background: '#ffffff',
                backgroundSecondary: '#f8fafc',
                surface: '#ffffff',
                surfaceVariant: '#f1f5f9',
                textPrimary: '#1e293b',
                textSecondary: '#64748b',
                textMuted: '#94a3b8',
                textOnPrimary: '#ffffff',
                textOnSecondary: '#ffffff',
                textOnBackground: '#1e293b',
                textOnSurface: '#1e293b',
                border: '#e2e8f0',
                borderLight: '#f1f5f9',
                divider: '#e2e8f0',
                disabled: '#cbd5e1',
                hover: '#f1f5f9',
                focus: '#3b82f6',
                active: '#3b82f6',
                shadow: 'rgba(0, 0, 0, 0.1)',
                overlay: 'rgba(0, 0, 0, 0.5)'
            },
            typography: {
                fontFamily: "'Roobert', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
                fontSizeBase: '16px',
                fontSizeSmall: '14px',
                fontSizeLarge: '18px',
                fontSizeXLarge: '24px',
                fontWeightLight: 300,
                fontWeightNormal: 400,
                fontWeightMedium: 500,
                fontWeightBold: 700,
                lineHeightBase: 1.5,
                lineHeightSmall: 1.4,
                lineHeightLarge: 1.6
            },
            spacing: {
                xs: '4px',
                sm: '8px',
                md: '16px',
                lg: '24px',
                xl: '32px',
                xxl: '48px'
            },
            borderRadius: {
                none: '0',
                sm: '4px',
                md: '8px',
                lg: '12px',
                xl: '16px',
                full: '9999px'
            },
            shadows: {
                none: 'none',
                sm: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
                md: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                lg: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
                xl: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
            },
            breakpoints: {
                xs: '0px',
                sm: '576px',
                md: '768px',
                lg: '992px',
                xl: '1200px',
                xxl: '1400px'
            },
            assets: {
                logo: 'assets/cafler-logo.svg',
                logoWhite: 'assets/cafler-logo-white.svg',
                logoSmall: 'assets/cafler-logo-small.svg',
                favicon: 'assets/cafler-logo.svg',
                assetsBaseUrl: 'assets/'
            },
            components: this.getDefaultComponentStyles()
        };
    }

    /**
     * Obtiene los estilos por defecto de los componentes
     */
    private getDefaultComponentStyles(): ComponentStyles {
        return {
            buttons: {
                primary: {
                    backgroundColor: '#3b82f6',
                    textColor: '#ffffff',
                    borderColor: '#3b82f6',
                    borderRadius: '8px',
                    padding: '12px 24px',
                    fontSize: '16px',
                    fontWeight: 500,
                    hoverBackgroundColor: '#1d4ed8',
                    hoverTextColor: '#ffffff'
                },
                secondary: {
                    backgroundColor: '#f1f5f9',
                    textColor: '#475569',
                    borderColor: '#e2e8f0',
                    borderRadius: '8px',
                    padding: '12px 24px',
                    fontSize: '16px',
                    fontWeight: 500,
                    hoverBackgroundColor: '#e2e8f0',
                    hoverTextColor: '#334155'
                },
                outline: {
                    backgroundColor: 'transparent',
                    textColor: '#3b82f6',
                    borderColor: '#3b82f6',
                    borderRadius: '8px',
                    padding: '12px 24px',
                    fontSize: '16px',
                    fontWeight: 500,
                    hoverBackgroundColor: '#3b82f6',
                    hoverTextColor: '#ffffff'
                }
            },
            inputs: {
                backgroundColor: '#ffffff',
                textColor: '#1e293b',
                borderColor: '#d1d5db',
                borderRadius: '8px',
                padding: '12px 16px',
                fontSize: '16px',
                borderWidth: '1px',
                focusBorderColor: '#3b82f6',
                placeholderColor: '#9ca3af'
            },
            cards: {
                backgroundColor: '#ffffff',
                borderColor: '#e5e7eb',
                borderRadius: '12px',
                padding: '24px',
                shadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
                headerBackgroundColor: '#f9fafb',
                headerTextColor: '#111827'
            },
            navigation: {
                backgroundColor: '#ffffff',
                textColor: '#374151',
                activeColor: '#3b82f6',
                hoverColor: '#f3f4f6',
                borderColor: '#e5e7eb',
                logoHeight: '40px',
                menuItemPadding: '12px 16px'
            },
            sidebar: {
                backgroundColor: '#ffffff',
                textColor: '#374151',
                borderColor: '#e5e7eb',
                activeBackgroundColor: '#eff6ff',
                activeTextColor: '#3b82f6',
                hoverBackgroundColor: '#f9fafb',
                hoverTextColor: '#111827',
                width: '280px'
            },
            tables: {
                headerBackgroundColor: '#f9fafb',
                headerTextColor: '#111827',
                rowBackgroundColor: '#ffffff',
                rowAlternateBackgroundColor: '#f9fafb',
                rowTextColor: '#374151',
                borderColor: '#e5e7eb',
                hoverBackgroundColor: '#f3f4f6'
            },
            modals: {
                backgroundColor: '#ffffff',
                overlayColor: 'rgba(0, 0, 0, 0.5)',
                borderRadius: '12px',
                shadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
                headerBackgroundColor: '#f9fafb',
                headerTextColor: '#111827'
            }
        };
    }

    // Métodos helper para manipulación de colores
    private lightenColor(color: string, factor: number): string {
        const normalized = this.normalizeHexColor(color);
        if (!normalized) return color;

        const safeFactor = Math.max(-1, Math.min(1, factor));
        const hex = normalized.replace('#', '');
        const num = parseInt(hex, 16);
        const amt = Math.round(2.55 * safeFactor * 100);
        const R = (num >> 16) + amt;
        const G = (num >> 8 & 0x00FF) + amt;
        const B = (num & 0x0000FF) + amt;

        return '#' + (0x1000000 + (R < 255 ? R < 1 ? 0 : R : 255) * 0x10000 +
            (G < 255 ? G < 1 ? 0 : G : 255) * 0x100 +
            (B < 255 ? B < 1 ? 0 : B : 255)).toString(16).slice(1);
    }

    private darkenColor(color: string, factor: number): string {
        return this.lightenColor(color, -factor);
    }

    private extractBaseUrl(fullUrl?: string): string | undefined {
        if (!fullUrl) return undefined;
        const trimmed = fullUrl.trim();
        if (!trimmed) return undefined;

        if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
            try {
                const url = new URL(trimmed);
                return `${url.protocol}//${url.host}${url.pathname.substring(0, url.pathname.lastIndexOf('/') + 1)}`;
            } catch {
                return undefined;
            }
        }

        const lastSlashIndex = trimmed.lastIndexOf('/');
        if (lastSlashIndex === -1) return undefined;
        return trimmed.substring(0, lastSlashIndex + 1);
    }

    private getButtonBorderRadius(style: string): string {
        switch (style) {
            case 'rounded': return '8px';
            case 'square': return '0px';
            case 'pill': return '9999px';
            default: return '8px';
        }
    }

    private getNonEmptyString(value?: string | null): string | undefined {
        if (typeof value !== 'string') return undefined;
        const trimmed = value.trim();
        return trimmed.length > 0 ? trimmed : undefined;
    }

    private normalizeHexColor(color: string): string | null {
        const trimmed = color.trim();
        if (!trimmed) return null;

        const raw = trimmed.startsWith('#') ? trimmed.slice(1) : trimmed;
        if (/^[0-9a-fA-F]{3}$/.test(raw)) {
            const expanded = raw.split('').map((c) => c + c).join('');
            return `#${expanded}`;
        }
        if (/^[0-9a-fA-F]{6}$/.test(raw)) {
            return `#${raw}`;
        }

        return null;
    }

    private cloneComponents(defaultComponents: ComponentStyles): ComponentStyles {
        if (typeof structuredClone === 'function') {
            return structuredClone(defaultComponents);
        }
        return JSON.parse(JSON.stringify(defaultComponents));
    }
}
